// src/lib/websocket-client.js
import { writable, get } from 'svelte/store';
import { user, accessToken } from '$lib/stores/store';
import ApiClient from '$lib/api';
const api = new ApiClient();

export const status = writable('🔌 Connecting...');
export const users = writable([]);
export const messages = writable([]);
export const hasMore = writable(true);

export const myId = writable(null);
export const myName = writable('');

export const conversations = writable([]);
export const conversationId = writable(null);
export const conversationReminders = writable({ conversationId: null, rows: [] });
export const conversationBoardItems = writable({ conversationId: null, rows: [] });
export const pinnedMessages = writable({ conversationId: null, rows: [], loading: false, error: '' });
export const oldestMessageId = writable(null);
export const replyingTo = writable(null);

export const selectionMode = writable(false);
export const selectedMessageIds = writable(new Set());
export const typingUsers = writable({});
export const callEvents = writable(null);
//export const shareMessageContext = writable(null);
import { isFile, isPDF, isImage, isVideo, isDoc, isLink, hasLink } from '$lib/utils';

const messageIdSet = new Set();

// 🔥 Listen to user store changes
user.subscribe(u => {
    if (u) {
        myId.set(u.id);
        myName.set(u.name);
    } else {
        myId.set(null);
        myName.set('');
    }
});

const WS_URL = import.meta.env.VITE_WS_URL;

function isLocalHostname(hostname) {
    return ['localhost', '127.0.0.1', '0.0.0.0'].includes(String(hostname || '').toLowerCase());
}

function normalizeWebSocketUrl(url) {
    if (!url) return '';

    try {
        const resolved = new URL(url, typeof location !== 'undefined' ? location.href : 'http://localhost');

        if (!resolved.pathname || resolved.pathname === '/') {
            resolved.pathname = '/ws';
        }

        return resolved.toString().replace(/\/$/, '');
    } catch {
        return String(url).replace(/\/$/, '');
    }
}

function resolveWebSocketCandidates() {
    if (WS_URL) return [normalizeWebSocketUrl(WS_URL)];

    const apiBase = import.meta.env.VITE_API_BASE_URL;
    if (apiBase) {
        try {
            const resolved = new URL(apiBase);
            const wsProtocol = resolved.protocol === 'https:' ? 'wss:' : 'ws:';
            return [normalizeWebSocketUrl(`${wsProtocol}//${resolved.host}/ws`)];
        } catch {
            // fall through to local candidates
        }
    }

    if (typeof location === 'undefined') {
        return ['ws://localhost:4000/ws', 'wss://localhost:4443/ws'];
    }

    if (!isLocalHostname(location.hostname)) {
        const currentWsOrigin = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`;
        return [normalizeWebSocketUrl(`${currentWsOrigin}/ws`)];
    }

    const localFallbacks = location.protocol === 'https:'
        ? ['wss://localhost:4443/ws', 'ws://localhost:4000/ws']
        : ['ws://localhost:4000/ws', 'wss://localhost:4443/ws'];

    return [...new Set(localFallbacks.map(normalizeWebSocketUrl))];
}

let socket = null;
let reconnectTimer = null;
let manualClose = false; // 👈 quan trọng
let callEventSequence = 0;
const activeCallSync = new Set();
let wsCandidates = resolveWebSocketCandidates();
let wsCandidateIndex = 0;
const CALL_EVENT_TYPES = new Set([
    "call_invite",
    "call_ringing",
    "call_accept",
    "call_end",
    "call_peer_joined",
    "call_signal",
    "call_resume",
    "call_status"
]);
const pendingMessageLoads = new Map();
const pendingMessageContextLoads = new Map();
const pendingReactionDetailLoads = new Map();
const reactionDetailsCache = new Map();
const queuedChatMessages = [];
const pendingChatMessages = new Map();
const MESSAGE_LOAD_TIMEOUT = 8000;
const REACTION_DETAILS_CACHE_TTL = 30000;
const MAX_QUEUED_CHAT_MESSAGES = 50;
const CHAT_ACK_TIMEOUT_MS = 6000;
const WS_HEARTBEAT_INTERVAL_MS = 10000;
const WS_STALE_AFTER_MS = 25000;
let reactionDetailSequence = 0;
const USER_ACTION_TIMEOUT = 15000;
let pendingUserSearch = null;
let pendingConversationAction = null;
let wsAuthenticated = false;
let heartbeatTimer = null;
let lastServerActivityAt = 0;

const CONVERSATION_ACTION_RESPONSES = {
    create_direct_conversation: new Set(["conversation_ready"]),
    create_group_conversation: new Set(["conversation_ready"]),
    add_group_members: new Set(["conversation_ready"]),
    remove_group_member: new Set(["conversation_ready"]),
    transfer_group_owner: new Set(["conversation_ready"]),
    update_conversation_notifications: new Set(["conversation_ready"]),
    dissolve_group: new Set(["conversation_left"]),
    leave_conversation: new Set(["conversation_left"]),
    clear_conversation_history: new Set(["conversation_history_cleared"]),
    load_conversation_reminders: new Set(["conversation_reminders"]),
    create_conversation_reminder: new Set(["conversation_reminders"]),
    update_conversation_reminder_status: new Set(["conversation_reminders"]),
    load_conversation_board: new Set(["conversation_board_items"]),
    create_conversation_board_item: new Set(["conversation_board_items"]),
    delete_conversation_board_item: new Set(["conversation_board_items"]),
    vote_conversation_poll: new Set(["conversation_board_items"])
};

const USER_SEARCH_EVENT = "user-search";
const USER_SEARCH_LEGACY_EVENT = "search_users";
const USER_SEARCH_RESULT_EVENT = "user-search-result";
const USER_SEARCH_LEGACY_RESULT_EVENT = "user_search_results";
const USER_SEARCH_ERROR_EVENT = "user-search-error";
const USER_SEARCH_LEGACY_ERROR_EVENT = "user_search_error";
const WS_DEBUG = import.meta.env.DEV || import.meta.env.VITE_WS_DEBUG === 'true';

function debugWs(event, details = {}) {
    if (!WS_DEBUG) return;
    console.debug(`[messenger-ws] ${event}`, details);
}

function getConversationResponseId(data = {}) {
    return data.conversationId
        || data.conversation?.conversationId
        || data.conversation?.id
        || null;
}

function takePendingConversationAction(responseType, data = {}) {
    const pending = pendingConversationAction;
    if (!pending) return null;

    const expectedResponses = CONVERSATION_ACTION_RESPONSES[pending.payloadType];
    if (expectedResponses && !expectedResponses.has(responseType)) return null;

    const pendingConversationId = pending.conversationId ? String(pending.conversationId) : '';
    const responseConversationId = getConversationResponseId(data);
    if (
        pendingConversationId
        && responseConversationId
        && pendingConversationId !== String(responseConversationId)
    ) {
        return null;
    }

    clearTimeout(pending.timeout);
    pendingConversationAction = null;
    return pending;
}

function getCurrentWebSocketUrl() {
    return wsCandidates[wsCandidateIndex] || wsCandidates[0];
}

function advanceWebSocketCandidate() {
    if (WS_URL || wsCandidates.length <= 1) return;
    wsCandidateIndex = (wsCandidateIndex + 1) % wsCandidates.length;
}

function resolveMessageLoad(conversation, rows = []) {
    const key = String(conversation ?? '');
    const pending = pendingMessageLoads.get(key);
    if (!pending) return;

    clearTimeout(pending.timeout);
    pending.resolve(rows);
    pendingMessageLoads.delete(key);
}

function resolveAllMessageLoads({ preserveMessageLoads = false } = {}) {
    // During transient network switches, keep message-load requests alive so they can be resent after welcome.
    if (!preserveMessageLoads) {
        for (const [key, pending] of pendingMessageLoads) {
            clearTimeout(pending.timeout);
            pending.resolve([]);
            pendingMessageLoads.delete(key);
        }
    }

    for (const [key, pending] of pendingMessageContextLoads) {
        clearTimeout(pending.timeout);
        pending.resolve([]);
        pendingMessageContextLoads.delete(key);
    }

    for (const [key, pending] of pendingReactionDetailLoads) {
        clearTimeout(pending.timeout);
        pending.reject(new Error("Kết nối đã đóng. Vui lòng thử lại."));
        pendingReactionDetailLoads.delete(key);
    }

    if (pendingUserSearch) {
        clearTimeout(pendingUserSearch.timeout);
        pendingUserSearch.reject(new Error("Kết nối đã đóng. Vui lòng thử lại."));
        pendingUserSearch = null;
    }

    if (pendingConversationAction) {
        clearTimeout(pendingConversationAction.timeout);
        pendingConversationAction.reject(new Error("Kết nối đã đóng. Vui lòng thử lại."));
        pendingConversationAction = null;
    }
}

function queueChatMessage(data) {
    if (!data || data.type !== "chat") return false;

    // Only plain chat messages are queued during reconnect; destructive actions must not replay late.
    queuedChatMessages.push(prepareChatPayload(data));
    if (queuedChatMessages.length > MAX_QUEUED_CHAT_MESSAGES) {
        queuedChatMessages.splice(0, queuedChatMessages.length - MAX_QUEUED_CHAT_MESSAGES);
    }
    return true;
}

function flushQueuedChatMessages() {
    if (!socket || socket.readyState !== WebSocket.OPEN || !wsAuthenticated) return;

    while (queuedChatMessages.length > 0 && socket.readyState === WebSocket.OPEN) {
        sendChatPayload(queuedChatMessages.shift());
    }
}

function prepareChatPayload(data) {
    return {
        ...data,
        clientMessageId: data.clientMessageId || createClientMessageId()
    };
}

function createClientMessageId() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return `msg:${crypto.randomUUID()}`;
    }
    return `msg:${Date.now().toString(36)}:${Math.random().toString(36).slice(2, 10)}`;
}

function sendChatPayload(data) {
    if (!data || data.type !== "chat") return false;
    const payload = prepareChatPayload(data);

    if (!socket || socket.readyState !== WebSocket.OPEN || !wsAuthenticated) {
        return queueChatMessage(payload);
    }

    clearChatAckTimer(payload.clientMessageId);
    pendingChatMessages.set(payload.clientMessageId, {
        payload,
        sentAt: Date.now()
    });
    try {
        socket.send(JSON.stringify(payload));
        scheduleChatAckTimeout(payload.clientMessageId);
    } catch {
        forceReconnectSocket("chat-send-failed");
    }
    return true;
}

function scheduleChatAckTimeout(clientMessageId) {
    const pending = pendingChatMessages.get(clientMessageId);
    if (!pending) return;

    pending.timeout = setTimeout(() => {
        if (!pendingChatMessages.has(clientMessageId)) return;
        forceReconnectSocket("chat-ack-timeout");
    }, CHAT_ACK_TIMEOUT_MS);
}

function clearChatAckTimer(clientMessageId) {
    const pending = pendingChatMessages.get(clientMessageId);
    if (pending?.timeout) clearTimeout(pending.timeout);
}

function markChatAck(clientMessageId) {
    if (!clientMessageId) return;
    clearChatAckTimer(clientMessageId);
    pendingChatMessages.delete(clientMessageId);
}

function flushPendingChatMessages() {
    if (!socket || socket.readyState !== WebSocket.OPEN || !wsAuthenticated) return;

    for (const pending of pendingChatMessages.values()) {
        clearChatAckTimer(pending.payload.clientMessageId);
        try {
            socket.send(JSON.stringify(pending.payload));
            pending.sentAt = Date.now();
            scheduleChatAckTimeout(pending.payload.clientMessageId);
        } catch {
            forceReconnectSocket("pending-chat-send-failed");
            return;
        }
    }
}

function sendLoadMessagesRequest(key, pending) {
    if (!socket || socket.readyState !== WebSocket.OPEN || !pending) return false;

    clearTimeout(pending.timeout);
    pending.timeout = setTimeout(() => {
        resolveMessageLoad(pending.conversationId, []);
    }, MESSAGE_LOAD_TIMEOUT);

    socket.send(JSON.stringify({
        type: "load_messages",
        conversationId: pending.conversationId,
        firstMsg: pending.opts?.firstMsg ?? null,
    }));
    debugWs("load_messages sent", { conversationId: pending.conversationId, key });
    return true;
}

function resendPendingMessageLoads() {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    for (const [key, pending] of pendingMessageLoads) {
        try {
            sendLoadMessagesRequest(key, pending);
        } catch (error) {
            console.error("Reload messages request failed:", error);
            resolveMessageLoad(pending.conversationId, []);
        }
    }
}

function refreshActiveConversationAfterReconnect() {
    const activeConversationId = get(conversationId);
    if (!activeConversationId) return;

    loadMessagesWS(activeConversationId);
    loadPinnedMessagesWS(activeConversationId);
}

function markServerActivity() {
    lastServerActivityAt = Date.now();
}

function startHeartbeat() {
    stopHeartbeat();
    markServerActivity();
    heartbeatTimer = setInterval(() => {
        if (!socket || socket.readyState !== WebSocket.OPEN) return;

        if (Date.now() - lastServerActivityAt > WS_STALE_AFTER_MS) {
            forceReconnectSocket("heartbeat-timeout");
            return;
        }

        socket.send(JSON.stringify({
            type: "client_ping",
            ts: Date.now()
        }));
    }, WS_HEARTBEAT_INTERVAL_MS);
}

function stopHeartbeat() {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
}

function forceReconnectSocket(reason = "stale-connection") {
    if (!socket || socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) return;

    debugWs("force reconnect", { reason });
    status.set("🔴 Connection stale — reconnecting...");
    try {
        socket.close(4000, reason);
    } catch {
        socket = null;
        clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(connectWebSocket, 250);
    }
}
/* ========================================================
   🚀 CONNECT WEBSOCKET — WITH COOKIE (refreshToken)
   Backend sẽ đọc cookie refreshToken để login WS
======================================================== */
export function connectWebSocket() {
    console.log('connectWebSocket....', socket?.readyState, WebSocket.CLOSED)
    if (socket && socket.readyState !== WebSocket.CLOSED) {
        console.log("⚙️ WS already connected or connecting");
        return socket;
    }

    const token = get(accessToken);
    // console.log('ws connect token:', token)
    if (!token) {
        console.log("No access token — DO NOT CONNECT WS yet.");
        return;
    }
    manualClose = false; // 👈 reset khi connect mới
    wsCandidates = resolveWebSocketCandidates();
    wsAuthenticated = false;
    socket = new WebSocket(getCurrentWebSocketUrl());

    socket.onopen = () => {
        startHeartbeat();
        socket.send(JSON.stringify({
            type: "auth",
            token: token
        }));
        status.set("🟡 Authenticating...");
        debugWs("open", { url: getCurrentWebSocketUrl() });
    };


    /* ========================================================
       📥 RECEIVE MESSAGE
    ======================================================== */
    socket.onmessage = async (event) => {
        try {
            markServerActivity();
            const data = JSON.parse(event.data);

            //console.log('data msg:', data)

            if (data.type === "server_pong") {
                return;
            }

            if (CALL_EVENT_TYPES.has(data.type)) {
                callEvents.set({
                    ...data,
                    eventId: ++callEventSequence,
                    receivedAt: Date.now()
                });
                return;
            }

            if (data.type === 'auth_error') {
                wsAuthenticated = false;
                debugWs("auth_error", { message: data.message });

                console.warn('Token expired — refreshing...', new Date().toISOString());

                try {
                    const res = await api.post(
                        '/auth/refresh',
                        {},
                        { withCredentials: true }
                    );

                    const newToken = res.accessToken;
                    accessToken.set(newToken);

                    sendWhenOpen({
                        type: 'auth',
                        token: newToken
                    });

                } catch (err) {
                    console.error('Lỗi Refresh token failed:', err);
                    socket.close();
                }
                return;
            }

            //console.log('data::', data)

            if (data.type === "recent_contacts") {
                //console.log('recent_contacts:', data.contacts)
                conversations.set([...data.conversations]);
                return;
            }

            if (data.type === "user_avatar_updated") {
                const currentUser = get(user);
                if (currentUser && String(currentUser.id) === String(data.userId)) {
                    user.update((current) => ({ ...current, avatar: data.avatar || '' }));
                }
                return;
            }

            if (data.type === USER_SEARCH_RESULT_EVENT || data.type === USER_SEARCH_LEGACY_RESULT_EVENT) {
                const pending = pendingUserSearch;
                if (!pending) return;

                clearTimeout(pending.timeout);
                pending.resolve(Array.isArray(data.users) ? data.users : []);
                pendingUserSearch = null;
                debugWs("user-search result", {
                    count: Array.isArray(data.users) ? data.users.length : 0
                });
                return;
            }

            if (data.type === USER_SEARCH_ERROR_EVENT || data.type === USER_SEARCH_LEGACY_ERROR_EVENT) {
                const pending = pendingUserSearch;
                if (!pending) return;

                clearTimeout(pending.timeout);
                pending.reject(new Error(data.message || "Không thể tìm kiếm người dùng."));
                pendingUserSearch = null;
                debugWs("user-search error", {
                    message: data.message
                });
                return;
            }

            if (data.type === "conversation_ready") {
                if (data.conversation) {
                    upsertConversation(data.conversation);
                }

                const pending = takePendingConversationAction("conversation_ready", data);
                if (!pending) return;

                pending.resolve(data.conversation);
                return;
            }

            if (data.type === "conversation_created") {
                if (data.conversation) {
                    upsertConversation(data.conversation);
                }
                return;
            }

            if (data.type === "conversation_left") {
                removeConversation(data.conversationId);

                const pending = takePendingConversationAction("conversation_left", data);
                if (!pending) return;

                pending.resolve({ conversationId: data.conversationId });
                return;
            }

            if (data.type === "conversation_history_cleared") {
                conversations.update(list =>
                    (Array.isArray(list) ? list : []).map(item =>
                        String(item.conversationId) === String(data.conversationId)
                            ? { ...item, lastMessageAt: null, lastMessagePreview: '' }
                            : item
                    )
                );

                if (String(get(conversationId) ?? '') === String(data.conversationId)) {
                    messages.set([]);
                }

                const pending = takePendingConversationAction("conversation_history_cleared", data);
                if (!pending) return;

                pending.resolve({ conversationId: data.conversationId });
                return;
            }

            if (data.type === "conversation_reminders") {
                conversationReminders.set({
                    conversationId: data.conversationId || null,
                    rows: Array.isArray(data.rows) ? data.rows : []
                });

                const pending = takePendingConversationAction("conversation_reminders", data);
                if (!pending) return;

                pending.resolve(Array.isArray(data.rows) ? data.rows : []);
                return;
            }

            if (data.type === "conversation_board_items") {
                conversationBoardItems.set({
                    conversationId: data.conversationId || null,
                    rows: Array.isArray(data.rows) ? data.rows : []
                });

                const pending = takePendingConversationAction("conversation_board_items", data);
                if (!pending) return;

                pending.resolve(Array.isArray(data.rows) ? data.rows : []);
                return;
            }

            if (data.type === "pinned_messages") {
                if (String(data.conversationId ?? '') !== String(get(conversationId) ?? '')) return;

                pinnedMessages.set({
                    conversationId: data.conversationId || null,
                    rows: Array.isArray(data.rows) ? data.rows : [],
                    loading: false,
                    error: ''
                });
                return;
            }

            if (data.type === "message_pin_error") {
                if (String(data.conversationId ?? '') !== String(get(conversationId) ?? '')) return;

                pinnedMessages.update(state => ({
                    ...state,
                    loading: false,
                    error: data.message || "Không thể cập nhật tin nhắn ghim."
                }));
                return;
            }

            if (data.type === "conversation_error") {
                const pending = pendingConversationAction;
                if (!pending) return;

                clearTimeout(pending.timeout);
                pending.reject(new Error(data.message || "Không thể tạo cuộc trò chuyện."));
                pendingConversationAction = null;
                return;
            }

            // Welcome message
            if (data.type === "welcome") {
                //console.log('welcome', data)
                //messages.update(m => [...m, data]);
                wsAuthenticated = true;
                for (const getCall of activeCallSync) {
                    const call = getCall();
                    if (call?.conversationId && call?.sessionId) {
                        socket.send(JSON.stringify({ type: "call_sync", ...call }));
                    }
                }
                status.set("🟢 Connected");
                debugWs("welcome", { userId: data.user?.id });
                resendPendingMessageLoads();
                refreshActiveConversationAfterReconnect();
                flushPendingChatMessages();
                flushQueuedChatMessages();
                return;
            }

            // Chat message
            if (data.type === "chat") {
                const msg = data?.payload ?? {};
                markChatAck(msg.clientMessageId);
                if (!isMessageForCurrentChat(msg)) return;

                //console.log('new msg ws:', msg)

                //messages.update(m => [...m, msg]);
                messages.update(m =>
                    appendMessages(m, [msg])
                );

                // 👉 Nếu là file / media → reload documents
                if (isDocumentMessage(msg)) {
                    const cid = msg.conversationId;
                    getDocumentsByConversation(cid);
                }

                return;
            }

            if (data.type === "message_ack") {
                const pending = pendingChatMessages.get(data.clientMessageId);
                markChatAck(data.clientMessageId);
                if (pending?.payload?.msgType === "media" && data.conversationId) {
                    void loadMessagesWS(String(data.conversationId));
                }
                return;
            }

            if (data.type === "message_error") {
                markChatAck(data.clientMessageId);
                status.set(data.message || "Không thể gửi tin nhắn.");
                return;
            }

            if (data.type === "typing_start") {
                const { conversationId, from } = data;

                typingUsers.update(map => {
                    const set = new Set(map[conversationId] || []);
                    set.add(from);
                    return { ...map, [conversationId]: set };
                });

                return;
            }

            if (data.type === "typing_stop") {
                const { conversationId, from } = data;

                typingUsers.update(map => {
                    const set = new Set(map[conversationId] || []);
                    set.delete(from);
                    return { ...map, [conversationId]: set };
                });

                return;
            }

            // if (data.type === "messages") {
            //     const { rows } = data;
            //     rows.reverse();
            //     console.log(rows)
            //     messages.set(rows);
            //     return;
            // }

            if (data.type === "messages") {
                const rows = Array.isArray(data.rows) ? data.rows : [];
                resolveMessageLoad(data.conversationId, rows);

                if (String(data.conversationId) !== String(get(conversationId))) return;

                hasMore.set(rows.length >= 10);
                if (rows.length === 0) return;

                messages.update(m =>
                    appendMessages(m, rows, { prepend: true })
                );
                return;
            }

            if (data.type === "reaction_update") {
                const { messageId, reactions, actorId, currentReaction } = data;
                const isMyUpdate = String(actorId ?? '') === String(get(myId) ?? '');

                reactionDetailsCache.delete(String(messageId));

                messages.update(list =>
                    list.map(m =>
                        m.id === messageId
                            ? {
                                ...m,
                                reactions,
                                myReaction: isMyUpdate ? (currentReaction ?? null) : m.myReaction
                            }
                            : m
                    )
                );
                return;
            }

            if (data.type === "message_reactors") {
                const key = String(data.messageId || '');
                if (!key) return;

                const details = {
                    messageId: data.messageId,
                    conversationId: data.conversationId || null,
                    total: Number(data.total || 0),
                    anonymousCount: Number(data.anonymousCount || 0),
                    hiddenCount: Number(data.hiddenCount || 0),
                    summary: Array.isArray(data.summary) ? data.summary : [],
                    rows: Array.isArray(data.rows) ? data.rows : []
                };
                reactionDetailsCache.set(key, {
                    data: details,
                    expiresAt: Date.now() + REACTION_DETAILS_CACHE_TTL
                });

                const pending = pendingReactionDetailLoads.get(key);
                if (!pending || (data.requestId && data.requestId !== pending.requestId)) return;
                clearTimeout(pending.timeout);
                pending.resolve(details);
                pendingReactionDetailLoads.delete(key);
                return;
            }

            if (data.type === "message_reactors_error") {
                const key = String(data.messageId || '');
                const pending = pendingReactionDetailLoads.get(key);
                if (!pending || (data.requestId && data.requestId !== pending.requestId)) return;
                clearTimeout(pending.timeout);
                pending.reject(new Error(data.message || "Không thể tải danh sách cảm xúc."));
                pendingReactionDetailLoads.delete(key);
                return;
            }

            if (data.type === "message_recalled") {
                const { messageId } = data;

                messages.update(list =>
                    list.map(m =>
                        m.id === messageId
                            ? { ...m, status: 1 }
                            : m
                    )
                );
                pinnedMessages.update(state => ({
                    ...state,
                    rows: state.rows.filter(row => row.id !== messageId)
                }));
                return;
            }

            if (data.type === "message_deleted_self") {
                const { messageId } = data;

                messages.update(list =>
                    list.filter(m => m.id !== messageId)
                );

                documents.update(docs =>
                    docs.filter(d => d.id !== messageId)
                );

                pinnedMessages.update(state => ({
                    ...state,
                    rows: state.rows.filter(row => row.id !== messageId)
                }));

                return;
            }

            // search message
            if (data.type === "search_results") {
                if (data.requestId !== activeSearchRequestId) return;
                clearTimeout(searchTimeout);

                const rows = Array.isArray(data.rows) ? data.rows : [];

                if (activeSearchAppend) {
                    searchResults.update(list => appendUniqueSearchRows(list, rows));
                } else {
                    searchResults.set(rows);
                }

                searchCursor.set(data.cursor || null);
                searchHasMore.set(Boolean(data.hasMore));
                searchLoading.set(false);
                return;
            }

            if (data.type === "search_error") {
                if (data.requestId !== activeSearchRequestId) return;
                clearTimeout(searchTimeout);
                searchError.set(data.message || "Không thể tìm kiếm tin nhắn.");
                searchLoading.set(false);
                return;
            }

            if (data.type === "message_context") {
                const pending = pendingMessageContextLoads.get(data.requestId);
                if (!pending) return;

                clearTimeout(pending.timeout);
                const rows = Array.isArray(data.rows) ? data.rows : [];
                pending.resolve(rows);
                pendingMessageContextLoads.delete(data.requestId);
                return;
            }


        } catch (err) {
            console.warn("⚠️ Invalid WS message:", event.data);
        }
    };

    /* ========================================================
       🔌 AUTO RECONNECT
    ======================================================== */
    socket.onerror = () => {
        status.set("🔴 Disconnected — retrying...");
    };

    socket.onclose = () => {
        wsAuthenticated = false;
        stopHeartbeat();
        const token = get(accessToken);
        resolveAllMessageLoads({ preserveMessageLoads: !manualClose && Boolean(token) });

        if (manualClose || !token) {
            console.log("🛑 WS closed manually or token missing — NO reconnect");
            status.set("⚪ WS closed");
            socket = null;
            return;
        }
        advanceWebSocketCandidate();
        console.log("🔴 WS disconnected — retry in 2s...");
        status.set("🔴 Disconnected — retrying...");
        socket = null;

        clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(connectWebSocket, 2000);
    };

    function sendWhenOpen(payload) {
        //console.log('sendWhenOpen:', socket.readyState === WebSocket.OPEN, payload)

        if (!socket) return;

        if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify(payload));
        } else {
            const onOpen = () => {
                socket.send(JSON.stringify(payload));
                socket.removeEventListener('open', onOpen);
            };
            socket.addEventListener('open', onOpen);
        }
    }

    function isDocumentMessage(msg) {
        const url = msg?.message;
        if (!url) return false;

        return (
            isImage(url) ||
            isVideo(url) ||
            isPDF(url) ||
            isDoc(url) ||
            isFile(url) ||
            hasLink(url) || isLink(url)
        );
    }

    return socket;
}

/* ========================================================
   📤 SEND MESSAGE (NO accessToken)
======================================================== */
function isMessageForCurrentChat(msg) {
    const currentConversationId = get(conversationId);

    if (!currentConversationId) return false;

    return String(msg.conversationId) === String(currentConversationId);
}

export function sendMessage(data) {
    if (data?.type === "chat") {
        if (!socket || socket.readyState !== WebSocket.OPEN || !wsAuthenticated) {
            console.warn("⚠️ WS not ready");
            if (queueChatMessage(data)) {
                status.set("🟡 Reconnecting — message queued");
                connectWebSocket();
                return true;
            }
            return false;
        }
        return sendChatPayload(data);
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
        console.warn("⚠️ WS not ready");
        return false;
    }
    //console.log('send:', data)
    socket.send(JSON.stringify(data));
    return true;
}

export function sendTyping(type = "start") {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;

    socket.send(JSON.stringify({
        type: type === "start" ? "typing_start" : "typing_stop",
        conversationId: get(conversationId)
    }));
}

/* ========================================================
   📤 BINARY SUPPORT
======================================================== */
export const sendChunk = (buffer) =>
    socket?.readyState === WebSocket.OPEN && socket.send(buffer);

export const sendStop = () =>
    socket?.readyState === WebSocket.OPEN && socket.send("stop");

/* ========================================================
   🔌 CLOSE MANUALLY
======================================================== */
export function closeSocket() {
    if (socket) {
        console.log("🔌 Closing WS manually...");
        manualClose = true; // 👈 đánh dấu close chủ ý
        clearTimeout(reconnectTimer);
        queuedChatMessages.splice(0);
        for (const pending of pendingChatMessages.values()) clearTimeout(pending.timeout);
        pendingChatMessages.clear();
        stopHeartbeat();
        resolveAllMessageLoads();
        socket.close();
        socket = null;
    }
}

export function loadMessagesWS(conversationId, opts = {}) {
    if (!socket || socket.readyState !== WebSocket.OPEN || !conversationId) {
        if (!conversationId || !get(accessToken)) return Promise.resolve([]);
        connectWebSocket();
        return enqueueMessageLoad(conversationId, opts);
    }

    return enqueueMessageLoad(conversationId, opts);
}

function enqueueMessageLoad(conversationId, opts = {}) {
    const key = String(conversationId);
    const existing = pendingMessageLoads.get(key);
    if (existing) return existing.promise;

    if (!opts.firstMsg) hasMore.set(true);

    let resolveRequest;
    const promise = new Promise((resolve) => {
        resolveRequest = resolve;
    });

    const timeout = setTimeout(() => {
        resolveMessageLoad(conversationId, []);
    }, MESSAGE_LOAD_TIMEOUT);

    pendingMessageLoads.set(key, {
        conversationId,
        opts,
        promise,
        resolve: resolveRequest,
        timeout
    });

    try {
        if (socket?.readyState === WebSocket.OPEN) {
            sendLoadMessagesRequest(key, pendingMessageLoads.get(key));
        }
    } catch (error) {
        console.error("Load messages request failed:", error);
        resolveMessageLoad(conversationId, []);
    }

    return promise;
}

export function loadPinnedMessagesWS(targetConversationId) {
    if (!targetConversationId) {
        pinnedMessages.set({ conversationId: null, rows: [], loading: false, error: '' });
        return false;
    }

    pinnedMessages.update(state =>
        String(state.conversationId ?? '') === String(targetConversationId)
            ? { ...state, loading: true, error: '' }
            : { conversationId: targetConversationId, rows: [], loading: true, error: '' }
    );

    const sent = sendMessage({
        type: "load_pinned_messages",
        conversationId: targetConversationId
    });

    if (!sent) {
        pinnedMessages.update(state => ({ ...state, loading: false }));
    }

    return sent;
}

export function pinMessageWS(targetConversationId, messageId) {
    if (!targetConversationId || !messageId) return false;

    pinnedMessages.update(state => ({ ...state, loading: true, error: '' }));
    const sent = sendMessage({
        type: "pin_message",
        conversationId: targetConversationId,
        messageId
    });

    if (!sent) pinnedMessages.update(state => ({ ...state, loading: false }));
    return sent;
}

export function unpinMessageWS(targetConversationId, messageId) {
    if (!targetConversationId || !messageId) return false;

    pinnedMessages.update(state => ({ ...state, loading: true, error: '' }));
    const sent = sendMessage({
        type: "unpin_message",
        conversationId: targetConversationId,
        messageId
    });

    if (!sent) pinnedMessages.update(state => ({ ...state, loading: false }));
    return sent;
}

export function loadMessageReactorsWS(messageId, { force = false } = {}) {
    const key = String(messageId || '');
    if (!key) return Promise.reject(new Error("Tin nhắn không hợp lệ."));

    const cached = reactionDetailsCache.get(key);
    if (!force && cached?.expiresAt > Date.now()) {
        return Promise.resolve(cached.data);
    }

    const existing = pendingReactionDetailLoads.get(key);
    if (existing) return existing.promise;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error("Kết nối chưa sẵn sàng."));
    }

    const requestId = `reactors-${Date.now()}-${++reactionDetailSequence}`;
    let resolveRequest;
    let rejectRequest;
    const promise = new Promise((resolve, reject) => {
        resolveRequest = resolve;
        rejectRequest = reject;
    });
    const timeout = setTimeout(() => {
        const pending = pendingReactionDetailLoads.get(key);
        if (pending?.requestId !== requestId) return;
        pendingReactionDetailLoads.delete(key);
        rejectRequest(new Error("Kết nối chậm. Vui lòng thử lại."));
    }, MESSAGE_LOAD_TIMEOUT);

    pendingReactionDetailLoads.set(key, {
        requestId,
        promise,
        resolve: resolveRequest,
        reject: rejectRequest,
        timeout
    });

    try {
        socket.send(JSON.stringify({
            type: "load_message_reactors",
            messageId: key,
            requestId
        }));
    } catch {
        clearTimeout(timeout);
        pendingReactionDetailLoads.delete(key);
        rejectRequest(new Error("Không gửi được yêu cầu."));
    }

    return promise;
}

/* ========================================================
   📥 LOAD MESSAGE HISTORY (Backend)
======================================================== */
export async function loadMessages(before = null) {
    try {
        const u = get(user);
        if (!u) return;

        let url = `${import.meta.env.VITE_API_BASE_URL}/messages?userId=${u.id}`;
        if (before) url += `&before=${before}`;

        const res = await fetch(url, {
            headers: {
                // Backend /messages cần accessToken
                Authorization: `Bearer ${localStorage.getItem("accessToken")}`
            },
            credentials: "include"
        });

        const rows = await res.json();

        if (rows.length < 50) hasMore.set(false);

        messages.update(m => [...rows, ...m]);

    } catch (err) {
        console.error("❌ loadMessages error:", err);
    }
}

export function appendMessages(oldList, incoming, { prepend = false } = {}) {
    const map = new Map();

    oldList.forEach(m => map.set(m.id, m));
    incoming.forEach(m => map.set(m.id, m));

    const merged = Array.from(map.values());

    return prepend
        ? merged.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
        : merged;
}

function upsertConversation(conversation) {
    if (!conversation?.conversationId) return;

    conversations.update(list => {
        const next = Array.isArray(list) ? [...list] : [];
        const index = next.findIndex(item => String(item.conversationId) === String(conversation.conversationId));

        if (index >= 0) {
            next[index] = { ...next[index], ...conversation };
        } else {
            next.unshift(conversation);
        }

        return next.sort((a, b) => {
            if (a.type === 'cloud') return -1;
            if (b.type === 'cloud') return 1;
            return new Date(b.lastMessageAt || b.createdAt || 0) - new Date(a.lastMessageAt || a.createdAt || 0);
        });
    });
}

function removeConversation(targetConversationId) {
    if (!targetConversationId) return;

    conversations.update(list =>
        (Array.isArray(list) ? list : []).filter(
            item => String(item.conversationId) !== String(targetConversationId)
        )
    );

    if (String(get(conversationId) ?? '') === String(targetConversationId)) {
        conversationId.set(null);
        messages.set([]);
    }
}

function sendConversationAction(payload, timeoutMs = USER_ACTION_TIMEOUT) {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error("Kết nối chưa sẵn sàng."));
    }

    return new Promise((resolve, reject) => {
        if (pendingConversationAction) {
            clearTimeout(pendingConversationAction.timeout);
            pendingConversationAction.reject(new Error("Yêu cầu cũ đã được thay bằng thao tác mới."));
            pendingConversationAction = null;
        }

        const timeout = setTimeout(() => {
            pendingConversationAction = null;
            debugWs("timeout", {
                payloadType: payload.type
            });
            reject(new Error("Kết nối chậm. Vui lòng thử lại."));
        }, timeoutMs);

        pendingConversationAction = {
            resolve,
            reject,
            timeout,
            payloadType: payload.type,
            conversationId: payload.conversationId || null
        };

        try {
            debugWs("send", {
                payloadType: payload.type
            });

            socket.send(JSON.stringify(payload));
        } catch (error) {
            clearTimeout(timeout);
            pendingConversationAction = null;
            reject(new Error("Không gửi được yêu cầu."));
        }
    });
}

export function searchUsersWS({ keyword = "", scope = "system", limit = 24, excludeExisting = false } = {}) {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        return Promise.reject(new Error("Kết nối chưa sẵn sàng."));
    }

    return new Promise((resolve, reject) => {
        if (pendingUserSearch) {
            clearTimeout(pendingUserSearch.timeout);
            pendingUserSearch.reject(new Error("Yêu cầu cũ đã được thay bằng từ khóa mới."));
            pendingUserSearch = null;
        }

        const timeout = setTimeout(() => {
            pendingUserSearch = null;
            debugWs("timeout", {
                type: USER_SEARCH_EVENT,
                payloadType: USER_SEARCH_LEGACY_EVENT
            });
            reject(new Error("Kết nối chậm. Vui lòng thử lại."));
        }, 8000);

        pendingUserSearch = { resolve, reject, timeout };

        try {
            debugWs("send", {
                type: USER_SEARCH_EVENT,
                payloadType: USER_SEARCH_LEGACY_EVENT
            });

            socket.send(JSON.stringify({
                type: USER_SEARCH_LEGACY_EVENT,
                keyword: String(keyword || "").trim(),
                scope,
                limit,
                excludeExisting
            }));
        } catch (error) {
            clearTimeout(timeout);
            pendingUserSearch = null;
            reject(new Error("Không gửi được yêu cầu."));
        }
    });
}

export function createDirectConversationWS(userId) {
    return sendConversationAction({
        type: "create_direct_conversation",
        targetUserId: userId
    });
}

export function createGroupConversationWS({ name, memberIds = [] } = {}) {
    return sendConversationAction({
        type: "create_group_conversation",
        name,
        memberIds
    });
}

export function leaveConversationWS(targetConversationId) {
    return sendConversationAction({
        type: "leave_conversation",
        conversationId: targetConversationId
    });
}

export function addGroupMembersWS(targetConversationId, memberIds = []) {
    return sendConversationAction({
        type: "add_group_members",
        conversationId: targetConversationId,
        memberIds
    });
}

export function removeGroupMemberWS(targetConversationId, memberId) {
    return sendConversationAction({
        type: "remove_group_member",
        conversationId: targetConversationId,
        memberId
    });
}

export function transferGroupOwnerWS(targetConversationId, memberId) {
    return sendConversationAction({
        type: "transfer_group_owner",
        conversationId: targetConversationId,
        memberId
    });
}

export function updateConversationNotificationsWS(targetConversationId, { mode = 'off', durationMinutes = null } = {}) {
    return sendConversationAction({
        type: "update_conversation_notifications",
        conversationId: targetConversationId,
        mode,
        durationMinutes
    });
}

export function dissolveGroupWS(targetConversationId) {
    return sendConversationAction({
        type: "dissolve_group",
        conversationId: targetConversationId
    });
}

export function clearConversationHistoryWS(targetConversationId) {
    return sendConversationAction({
        type: "clear_conversation_history",
        conversationId: targetConversationId
    });
}

export function loadConversationRemindersWS(targetConversationId) {
    return sendConversationAction({
        type: "load_conversation_reminders",
        conversationId: targetConversationId
    });
}

export function createConversationReminderWS(targetConversationId, { content, remindAt, repeatRule = 'none' } = {}) {
    return sendConversationAction({
        type: "create_conversation_reminder",
        conversationId: targetConversationId,
        content,
        remindAt,
        repeatRule
    });
}

export function updateConversationReminderStatusWS(targetConversationId, reminderId, status = 'done') {
    return sendConversationAction({
        type: "update_conversation_reminder_status",
        conversationId: targetConversationId,
        reminderId,
        status
    });
}

export function loadConversationBoardWS(targetConversationId) {
    return sendConversationAction({
        type: "load_conversation_board",
        conversationId: targetConversationId
    });
}

export function createConversationBoardItemWS(targetConversationId, { itemType = 'note', content, pinned = false, pollOptions = [] } = {}) {
    return sendConversationAction({
        type: "create_conversation_board_item",
        conversationId: targetConversationId,
        itemType,
        content,
        pinned,
        pollOptions
    });
}

export function deleteConversationBoardItemWS(targetConversationId, itemId) {
    return sendConversationAction({
        type: "delete_conversation_board_item",
        conversationId: targetConversationId,
        itemId
    });
}

export function voteConversationPollWS(targetConversationId, itemId, optionIndex) {
    return sendConversationAction({
        type: "vote_conversation_poll",
        conversationId: targetConversationId,
        itemId,
        optionIndex
    });
}

export async function activateConversation(conversation) {
    if (!conversation?.conversationId) return;

    upsertConversation(conversation);
    conversationId.set(conversation.conversationId);
    messages.set([]);
    pinnedMessages.set({ conversationId: conversation.conversationId, rows: [], loading: true, error: '' });
    await Promise.all([
        loadMessagesWS(conversation.conversationId),
        Promise.resolve(loadPinnedMessagesWS(conversation.conversationId))
    ]);
}

export async function uploadFile(file) {
    if (!file) throw new Error("File is required");

    const activeConversationId = get(conversationId);
    const form = new FormData();
    if (activeConversationId) {
        form.append("conversation_id", activeConversationId);
    }
    form.append("file", file);

    const uploadPath = activeConversationId
        ? `/api/upload?conversation_id=${encodeURIComponent(activeConversationId)}`
        : "/api/upload";
    return api.post(
        uploadPath,
        form,
        {
            withCredentials: true,
            responseType: "json"
        }
    );
}

export async function uploadImages(files) {
    const activeConversationId = get(conversationId);
    const form = new FormData();
    if (activeConversationId) {
        form.append("conversation_id", activeConversationId);
    }
    files.forEach(f => form.append("images", f));

    return api.post(
        "/api/upload/images",
        form,
        {
            withCredentials: true,
            responseType: "json"
        }
    );
}

export function getConversationById(conversations, conversationId) {
    if (!Array.isArray(conversations) || !conversationId) return null;

    return conversations.find(c => c.conversationId === conversationId) || null;
}

/* ========================================================
   📞 WEBRTC CALL SIGNALING
======================================================== */

/**
 * Join call room theo conversationId
 */
export function joinCall(conversationId) {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        console.warn("⚠️ WS not ready — cannot join call");
        return;
    }

    // socket.send(JSON.stringify({
    //     type: "join_call",
    //     conversationId
    // }));
}


/**
 * Gửi signaling WebRTC
 */
export function sendCallSignal(conversationId, data) {
    if (!socket || socket.readyState !== WebSocket.OPEN || !wsAuthenticated) {
        console.warn("⚠️ WS not ready — cannot send call signal");
        return false;
    }

    socket.send(JSON.stringify({
        type: "call_signal",
        conversationId,
        data
    }));
    return true;
}

export function registerCallSync(getCall) {
    activeCallSync.add(getCall);
    return () => activeCallSync.delete(getCall);
}

export function inviteCall(conversationId, callType = "video", options = {}) {
    if (!socket || socket.readyState !== WebSocket.OPEN || !conversationId) return false;

    socket.send(JSON.stringify({
        type: "call_invite",
        conversationId,
        callType,   // "video" | "audio"
        iceTransportPolicy: options.iceTransportPolicy,
        mediaRoute: options.mediaRoute
    }));
    return true;
}

export function acceptCall(conversationId) {
    if (!socket || socket.readyState !== WebSocket.OPEN || !conversationId) return false;

    socket.send(JSON.stringify({
        type: "call_accept",
        conversationId
    }));
    return true;
}

export function endCall(conversationId, message) {
    if (!socket || socket.readyState !== WebSocket.OPEN || !conversationId) return false;

    socket.send(JSON.stringify({
        type: "call_end",
        conversationId,
        message
    }));
    return true;
}

/**
 * Đăng ký nhận signaling WebRTC
 */
export function registerCallReceiver({
    onIncomingCall,
    onCallAccepted,
    onPeerJoined,
    onSignal,
    onCallEnded,
    onCallStatus,
    onCallResumed
}) {
    return callEvents.subscribe((msg) => {
        if (!msg) return;

        if (msg.type === "call_invite") {
            onIncomingCall?.(msg);
            return;
        }

        if (msg.type === "call_accept") {
            onCallAccepted?.(msg.conversationId, msg);
            return;
        }

        if (msg.type === "call_end") {
            onCallEnded?.(msg.conversationId, msg);
            return;
        }

        if (msg.type === "call_peer_joined") {
            onPeerJoined?.(msg.conversationId, msg.from);
            return;
        }

        if (msg.type === "call_signal") {
            onSignal?.(msg.conversationId, msg.data, msg);
            return;
        }

        if (msg.type === "call_resume") {
            onCallResumed?.(msg.conversationId, msg);
            return;
        }

        if (msg.type === "call_status" || msg.type === "call_ringing") {
            onCallStatus?.(msg.conversationId, msg);
        }
    });
}

export const documents = writable([]);
export const cloudStorage = writable(null);
export const shareContents = writable({});
export async function getDocumentsByConversation(conversationId) {
    const res = await api.get(`/api/conversations/${conversationId}/documents?userId=${get(myId)}`);
    documents.set(res.documents || []);
    cloudStorage.set(res.cloud || null);
    return res;
}

// ===== SEARCH STORE (WS SEARCH) =====
export const searchResults = writable([]);
export const searchCursor = writable(null);
export const searchLoading = writable(false);
export const searchHasMore = writable(true);
export const searchError = writable("");

let searchTimeout = null;
let activeSearchRequestId = null;
let activeSearchAppend = false;
let searchRequestSequence = 0;

function appendUniqueSearchRows(current, incoming) {
    const seen = new Set(current.map(row => row.id));
    return [...current, ...incoming.filter(row => !seen.has(row.id))];
}

export function searchMessagesWS({
    conversationId,
    keyword = "",
    senderFilter = "all",
    dateFilter = null,
    before = null,
    limit = 20
}) {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        searchError.set("Kết nối tìm kiếm chưa sẵn sàng.");
        searchLoading.set(false);
        return null;
    }

    const requestId = `search-${Date.now()}-${++searchRequestSequence}`;
    activeSearchRequestId = requestId;
    activeSearchAppend = Boolean(before);
    searchLoading.set(true);
    searchError.set("");

    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
        if (requestId !== activeSearchRequestId) return;
        searchError.set("Tìm kiếm mất nhiều thời gian hơn dự kiến. Vui lòng thử lại.");
        searchLoading.set(false);
    }, 8000);

    if (!before) {
        searchResults.set([]);
        searchCursor.set(null);
        searchHasMore.set(true);
    }

    socket.send(JSON.stringify({
        type: "search_messages",
        conversationId,
        keyword,
        senderFilter,
        dateFilter,
        before,
        limit,
        requestId
    }));

    return requestId;
}

export function resetSearch() {
    clearTimeout(searchTimeout);
    activeSearchRequestId = null;
    activeSearchAppend = false;
    searchResults.set([]);
    searchCursor.set(null);
    searchHasMore.set(true);
    searchLoading.set(false);
    searchError.set("");
}

export function loadMessageContextWS(conversationId, messageId) {
    if (!socket || socket.readyState !== WebSocket.OPEN || !conversationId || !messageId) {
        return Promise.resolve([]);
    }

    const requestId = `context-${Date.now()}-${++searchRequestSequence}`;

    return new Promise((resolve) => {
        const timeout = setTimeout(() => {
            pendingMessageContextLoads.delete(requestId);
            resolve([]);
        }, MESSAGE_LOAD_TIMEOUT);

        pendingMessageContextLoads.set(requestId, { resolve, timeout });
        socket.send(JSON.stringify({
            type: "load_message_context",
            conversationId,
            messageId,
            requestId
        }));
    });
}
